import { defineStore } from "pinia";
import SecureLs from "secure-ls";
import { computed, ref } from "vue";
import { IconReactionType } from "@/API";
import AppAuth from "@/aws-config";
import { LoadingManager } from "@/components/loading/loading-manager";
import type { AppKeys } from "@/direct-app-config";
import { EventManager } from "@/events/event-manager";
import type { Acl, Comment, Message, Topic, User } from "@/model";
import { ServerApiAccess } from "@/server-api-access";
import {
  type DownloadDeviceTypes,
  DownloadDeviceTypesDefault,
} from "@/suppport-attachment-types";
import GetDepartmentsTask from "@/tasks/get-departments-task";
import GetDomainMembersTask from "@/tasks/get-domain-members-task";
import GetTalksTask from "@/tasks/get-talks-task";
import { isAndroid, isIos, isMobile, osType } from "../mobile";
import { useCategoryStore } from "./category-store";
import { useCommentStore } from "./comment-store";
import { registerDemoData } from "./demo-data";
import { type SetPayload, useDomainStore } from "./domain-store";
import FeatureDao from "./feature-dao";
import { useMessageStore } from "./message-store";
import {
  type PayloadFetchTopicList,
  type PayloadUpdateReaction,
  useReactionStore,
} from "./reaction-store";
import { useTopicStore } from "./topic-store";
import { useUserStore } from "./user-store";

/** リアクションをstoreに設定するためのペイロード */
export type PayloadReaction = {
  target: Topic | Message | Comment;
  type: IconReactionType;
  value: boolean;
};

/** ソート ASC or DESC */
export type SORT_TYPE = "ASC" | "DESC";

export interface ISearchParam {
  word: string;
}

export type PersonalSetting = {
  sortComments: Record<string, SORT_TYPE>;
};

const DEFAULT_PEERSONAL_SETTING: PersonalSetting = {
  sortComments: {},
};

const ls = new SecureLs({ isCompression: false });
export { ls };

export const useAppStore = defineStore(
  "app",
  () => {
    // --- state ---
    const session = ref<boolean>(false); // 現在セッションがあるかどうか
    const domainId = ref<string>(""); // 現在選択中の組織ID
    const topicId = ref<string>(""); // 現在選択中の話題ID
    const messageId = ref<string>(""); // 現在選択中の投稿ID

    const navigationGuard = ref<boolean>(false);

    // 永続化
    const lastAccessPath = ref<string>("");
    const searchParam = ref<ISearchParam>({ word: "" });
    // 永続化(アプリ側からのURLクエリ)
    const fromQuery = ref<string>("");
    const lang = ref<string>("");
    const device = ref<string>("");
    const osversion = ref<string>("");
    const invokerapp = ref<string>("");
    const invokerversion = ref<string>("");
    const user_id = ref<string>("");

    const allowFeature = ref<{ [keyword: string]: string }>({}); // α／β機能表

    // acl関係
    const aclMap = ref<Record<string, Acl>>({});

    // indexedDBの復元に用いるsalt群
    const randomNumber1 = ref<number[]>([]); // indexeddb/webcrypt salt2に対応
    const randomNumber2 = ref<number[]>([]); // indexeddb/webcrypt salt3に対応
    const randomNumber3 = ref<number[]>([]); // indexeddb/webcrypt salt1に対応

    const loading = ref<boolean>(false);
    const fetching = ref<boolean>(false);

    const personalSetting = ref<PersonalSetting>(DEFAULT_PEERSONAL_SETTING);

    // --- getters ---
    // 注: device/fromQuery/searchParam/allowFeature/lastAccessPath/invokerapp は
    // state をそのまま読めば良いため、Vuex 時代の同名 getter 用エイリアスは設けない。
    const hasSession = computed(() => session.value);
    const salt1 = computed<Uint8Array>(() =>
      Uint8Array.from(randomNumber3.value),
    );
    const salt2 = computed<Uint8Array>(() =>
      Uint8Array.from(randomNumber1.value),
    );
    const salt3 = computed<Uint32Array>(() =>
      Uint32Array.from(randomNumber2.value),
    );

    // state 名(user_id)と Vuex getter 名(userId)が異なるためエイリアスを残す
    const userId = computed(() => user_id.value);

    // Utility
    const isMobileGetter = computed(() => isMobile(device.value));
    const isAndroidGetter = computed(() => isAndroid(device.value));
    const isIosGetter = computed(() => isIos(device.value));
    const os = computed(() => osType(device.value));
    const isPro = computed<boolean>(() => useDomainStore().isPro);

    // me (各ストアへ委譲するルート getter)
    const me = computed<User | undefined>(
      () => useUserStore().me as User | undefined,
    );
    const getUser = (cognitoUserId: string, dId?: string): User | undefined => {
      const arg = dId ? dId : domainId.value;
      return useUserStore().get(cognitoUserId, arg);
    };

    // topic
    const getTopics = (dId: string): Topic[] => useTopicStore().get(dId);
    const getTopic = (dId: string, tId: string): Topic | undefined =>
      useTopicStore().getOne(dId, tId);

    // message
    const getMessages = (dId: string, tId: string): Message[] =>
      useMessageStore().get(dId, tId) as Message[];
    const getMessage = (
      dId: string,
      tId: string,
      mId: string,
    ): Message | undefined => {
      const messages = useMessageStore().get(dId, tId) as Message[];
      return (messages || []).find((m) => m.id === mId);
    };

    // comment
    const getComments = (_dId: string, tId: string, mId: string) =>
      useCommentStore().get(tId, mId);
    const getComment = (
      _dId: string,
      tId: string,
      mId: string,
      cId: string,
    ): Comment | undefined => {
      const comments = useCommentStore().get(tId, mId) as Comment[];
      return (comments || []).find((c) => c.id === cId);
    };

    const getAclData = (id: string): Acl => {
      return aclMap.value[id];
    };

    const isLoading = computed(() => loading.value);

    const getSortCommentSetting = (dId: string): SORT_TYPE =>
      personalSetting.value.sortComments[dId] || "ASC";

    // --- 内部更新 (旧 mutations 相当) ---
    const setSessionMutation = (payload: { hasSession: boolean }): void => {
      session.value = payload.hasSession;
    };
    const setAclDataMutation = (aclData: {
      id: string;
      acl: Acl | undefined;
    }): void => {
      if (aclData.acl === undefined) {
        const { [aclData.id]: _, ...rest } = aclMap.value;
        aclMap.value = rest;
      } else {
        aclMap.value = { ...aclMap.value, [aclData.id]: aclData.acl };
      }
    };

    // --- actions ---
    const setSession = async (payload: {
      hasSession: boolean;
    }): Promise<void> => {
      setSessionMutation(payload);
    };

    /** Reactionを設定する */
    const setReaction = async (payload: PayloadReaction): Promise<void> => {
      const meUser = useUserStore().me as User;

      const reactionContext: PayloadUpdateReaction = {
        ...payload,
        userDirectId: meUser.directId,
      };
      useReactionStore().update(reactionContext);
    };

    // サーバ側から話題一覧を取得する
    const fetchTopics = async (): Promise<void> => {
      const dId = domainId.value;
      return await useTopicStore().fetch({ domainId: dId });
    };

    /** localStorageからデータを復元したもので元がクラスインスタンスのものを再生成する */
    const recreateData = async (): Promise<void> => {
      await useUserStore().restore();
      await useTopicStore().restore();
      await useDomainStore().restore();
    };

    // DBからデータロードする
    const _fetchData = async (payload?: {
      domainId: string;
      topicId?: string;
      prevDomainId?: string;
    }): Promise<void> => {
      const dId = payload?.domainId ?? "";
      const tId = payload?.topicId;
      const prevDomainId = payload?.prevDomainId;
      let _ownsFetching = false;
      try {
        if (LoadingManager.isLoading("on-fetch")) {
          return;
        }

        const isChangedDomain = dId !== prevDomainId; // 組織切替が発生したか
        if (dId) {
          LoadingManager.start("on-fetch", "読み込み中...");
        }
        // ※組織配下のカテゴリ及び話題データが投稿データ一覧でも必要
        switch (dId) {
          case "login":
          case "login-continue":
          case "login-continue-stg":
          case "login-continue-dev":
          case "login-mobile-redirect":
          case "logout":
            // ログイン関連処理時
            LoadingManager.stop("on-fetch");
            return;
        }
        fetching.value = true;
        _ownsFetching = true;
        console.info("{fetchData domainId:%s topicId:%s}", dId, tId);

        // 最終アクセスパスを覚えておく
        const lastPath = tId ? `/${dId}/${tId}` : `/${dId}`;
        if (dId) {
          setLastAccessPath(lastPath);
        }

        /* 自身の情報取得 */
        if (!LoadingManager.isEndInitialLoad()) {
          // 自分の状態を取得する
          const result: boolean = await useUserStore().fetchMe();
          if (!result) {
            throw "failed fetch me process.";
          }
        }

        /* indexedDBデータの復号 */
        if (!LoadingManager.isEndInitialLoad()) {
          await recreateData();
        }

        /* 個人設定のロード */
        const fetchPersonalSettingProcess = async () => {
          if (isChangedDomain || !LoadingManager.isEndInitialLoad()) {
            await fetchPersonalSetting();
          }
        };

        /* direct組織設定をロード */
        const fetchDirectDomainProcess = async () => {
          if (isChangedDomain || !LoadingManager.isEndInitialLoad()) {
            const result: boolean = await fetchDirectDomain();
            if (!result) {
              throw "failed fetch direct domain process.";
            }
          }
        };

        /* feature flagの取得 */
        const allowFeatureProcess = async () => {
          if (!LoadingManager.isEndInitialLoad()) {
            await loadAllowFeature();
          }
        };

        await Promise.all([
          fetchPersonalSettingProcess(),
          fetchDirectDomainProcess(),
          allowFeatureProcess(),
        ]);

        // 組織状態のチェック
        const isDomainEnable = useDomainStore().isEnable(dId);
        // domainIdが指定されていない時はアラートを出さない
        if (dId) {
          EventManager.allowedDomainEvent(!isDomainEnable);
        }
        if (!isDomainEnable) {
          if (dId) {
            LoadingManager.stop("on-fetch");
          }
          return; // 掲示板が有効ではないためロードしない
        }

        // 組織遷移した際のみ走る
        if (isChangedDomain || !LoadingManager.isEndInitialLoad()) {
          // directの組織に関連づいたアプリ一覧を取得する
          useDomainStore().fetchSolutions({ domainId: dId });
        }

        // カテゴリーデータ全取得
        const categories = useCategoryStore().get(dId);
        if (!categories || !LoadingManager.isEndInitialLoad()) {
          await useCategoryStore().fetch({ data: dId });
        }

        /* 並列処理の対象 */
        const topicProcess = async () => {
          // 話題データ全取得
          const topics = useTopicStore().get(dId);
          if (
            !topics ||
            (topics.length === 1 && topics.find((t: Topic) => t.id === tId)) ||
            !LoadingManager.isEndInitialLoad()
          ) {
            await useTopicStore().fetch({ domainId: dId });
          } else {
            useTopicStore().fetch({ domainId: dId });
          }
        };

        const topicReactionProcess = async () => {
          // 話題リアクションデータ全取得
          const type = IconReactionType.FAVORITE;
          const p: PayloadFetchTopicList = { domainId: dId, type };
          await useReactionStore().fetchDomainTopics(p);
        };

        const messageProcess = async () => {
          // 話題に紐付いた投稿データを全ロード
          const messages = useMessageStore().get(dId, tId as string);
          if (!messages || !LoadingManager.isEndInitialLoad()) {
            const mId = messageId.value ?? undefined;
            await useMessageStore().fetch({
              domainId: dId,
              topicId: tId as string,
              messageId: mId,
            });
          } else {
            const mId = messageId.value ?? undefined;
            useMessageStore().fetch({
              domainId: dId,
              topicId: tId as string,
              messageId: mId,
            });
          }
        };

        const commentProcess = async () => {
          const sort = getSortCommentSetting(dId);
          // 話題に紐付いたコメントデータを全取得
          const comments = useCommentStore().getByTopic(tId as string);
          if (!comments || !LoadingManager.isEndInitialLoad()) {
            await useCommentStore().fetch({ topicId: tId as string, sort });
          } else {
            useCommentStore().fetch({ topicId: tId as string, sort });
          }
        };

        const messageAndCommentReactionsProcess = async () => {
          if (!tId) return;
          // 投稿/コメントリアクションデータ全取得
          await useReactionStore().fetchMessageAndCommentReactions({
            domainId: dId,
            topicId: tId,
          });
        };

        const usersProcess = async () => {
          // ユーザー一覧の取得
          if (isChangedDomain || !LoadingManager.isEndInitialLoad()) {
            useUserStore().fetch({ data: dId });
          }
        };

        // 並列処理部
        if (tId === undefined || tId.length === 0) {
          await Promise.all([
            usersProcess(),
            topicProcess(),
            topicReactionProcess(),
          ]);
        } else {
          await Promise.all([
            usersProcess(),
            topicProcess(),
            topicReactionProcess(),
            messageProcess(),
            commentProcess(),
            messageAndCommentReactionsProcess(),
          ]);
        }
        LoadingManager.initalLoad();
        console.log("fetchData end.");
        LoadingManager.stop("on-fetch");
      } catch (err) {
        console.log("fetchData Error", err);
        LoadingManager.stop("on-fetch");
        setSession({ hasSession: false });
        if (err === "No current user") {
          EventManager.logoutEvent();
        }
        if (typeof err === "number" && err === 401) {
          // 認証エラーの場合は再ログインに流す
          EventManager.relogin();
        }
      } finally {
        if (_ownsFetching) {
          fetching.value = false;
        }
      }
    };

    /** direct組織設定をロード */
    const fetchDirectDomain = async (): Promise<boolean> => {
      const access = new ServerApiAccess();
      const domains = await access.listDomains();
      if (!domains) {
        return false;
      }
      await useDomainStore().setDomains(domains);

      await Promise.all(
        domains.map(async (domain) => {
          const allow_attachment_type =
            domain.setting.allow_attachment_type || 0;
          const allow_attachment_type_by_client =
            domain.setting.allow_attachment_type_by_client;
          const allowed_save_attachments_device_type = (domain.setting
            .allow_save_attachments ||
            DownloadDeviceTypesDefault) as DownloadDeviceTypes;
          const open_talk_enabled = domain.setting.open_talk_enabled || false;

          const domainSetting: SetPayload = {
            allow_attachment_type: allow_attachment_type,
            allow_attachment_type_by_client: allow_attachment_type_by_client,
            allow_save_attachments: allowed_save_attachments_device_type,
            open_talk_enabled: open_talk_enabled,
            domainId: domain.domain_id_str,
            domainName: domain.domain_name,
            domainRole: domain.role || undefined,
            solution_ids: domain.contract.solution_ids || [],
            direct_app_keys: (domain.contract.direct_app_keys ||
              []) as AppKeys[],
          };
          await useDomainStore().setDomainSetting(domainSetting);
        }),
      );
      return true;
    };

    // 現状、閲覧権限モーダルのみで使用
    /** direct組織情報(部署, 組織参加者)取得タスク */
    const fetchDomainInfo = async (dId: string): Promise<void> => {
      const getDepartmentsTask = async () => {
        await GetDepartmentsTask.run(dId);
      };
      const getDomainMembersTask = async () => {
        await GetDomainMembersTask.run(dId);
      };
      await Promise.all([getDepartmentsTask(), getDomainMembersTask()]);
    };

    // 同期処理
    const syncLoad = async (): Promise<void> => {
      const dId = domainId.value;
      const tId = topicId.value;
      LoadingManager.resetInitalLoad();
      LoadingManager.start("on-reload", "同期中...", "rgba(255,255,255,.7)");
      const acceess = new ServerApiAccess();

      // 権限のチェック(状態に寄らずチェックする)
      const status = await AppAuth.getServerUserInfo();
      if (status === 401) {
        LoadingManager.stop("on-reload");
        EventManager.relogin();
        throw 401;
      }

      await acceess.upsertDepartments(dId);
      await _fetchData({ domainId: dId, topicId: tId });
      LoadingManager.stop("on-reload");
    };

    /** direct組織IDをsetする */
    const setDomainId = async (dId: string): Promise<void> => {
      domainId.value = dId;
    };

    /** topicIdをsetする */
    const setTopicId = async (tId: string): Promise<void> => {
      topicId.value = tId;
    };

    /** messageIdをsetする */
    const setMessageId = async (mId: string): Promise<void> => {
      messageId.value = mId;
    };

    /** デモデータをDBから削除する */
    const removeDemoData = async (): Promise<void> => {
      await _fetchData();
      await useTopicStore().delete({
        topicId: "1",
        param: { deleted: true, deletedUser: "" },
      } as never);
    };

    /** デモデータの登録作業 */
    const registerDemoDataAction = async (
      type: "category" | "topic" | "message" | "comment",
    ): Promise<void> => {
      await registerDemoData(type);
    };

    /** 検索パラメータを保存 */
    const setSearchParam = async (param: ISearchParam): Promise<void> => {
      searchParam.value = param;
    };

    /** 検索パラメータの取得 */
    const getSearchParam = async (): Promise<ISearchParam> => {
      return searchParam.value;
    };

    const setNavigationGuard = async (guard: boolean): Promise<void> => {
      navigationGuard.value = guard;
      // ブラウザのページ離脱アラートをセット
      window.onbeforeunload = guard
        ? () => {
            return "離脱";
          }
        : null;
      console.log("navigationGuard", guard);
    };

    /** デバイス情報を保存 */
    const setDevice = async (d: string = ""): Promise<void> => {
      device.value = d ?? "";
    };

    /** UserId を設定 */
    const setUserId = async (userId: string): Promise<void> => {
      if (typeof userId === "string" && 0 < userId.length)
        user_id.value = userId;
    };

    /** InvokerApp情報を保存 */
    const setInvokerApp = async (invoker: string = ""): Promise<void> => {
      invokerapp.value = invoker ?? "";
    };

    // 永続化(アプリ側からのURLクエリ)
    const setFromQuery = async (query: {
      [key: string]: string;
    }): Promise<void> => {
      const str = Object.keys(query)
        .filter((key) => key !== "domain_id") // domain_idは残さない
        .map((key) => `${key}=${query[key]}`)
        .join("&");
      if (str) fromQuery.value = str ?? "";
    };

    /** 最終アクセスパス */
    const setLastAccessPath = async (path: string): Promise<void> => {
      lastAccessPath.value = path || "";
    };

    /** α／β機能表をロード */
    const loadAllowFeature = async (): Promise<void> => {
      const result = await FeatureDao.list();
      allowFeature.value = result;
    };

    const setAclData = async (aclData: {
      id: string;
      acl: Acl | undefined;
    }): Promise<void> => {
      setAclDataMutation(aclData);
    };

    /** 個人設定の取得 */
    const fetchPersonalSetting = async (): Promise<void> => {
      const api = new ServerApiAccess();
      const result = await api.getPersonalSetting();
      if (result) {
        personalSetting.value = result;
      }
    };

    /** コメントソート順の設定保存 */
    const setSortCommentSetting = async ({
      domainId: dId,
      sort,
    }: {
      domainId: string;
      sort: SORT_TYPE;
    }): Promise<void> => {
      const originSortComments = personalSetting.value.sortComments;
      originSortComments[dId] = sort;
      const api = new ServerApiAccess();
      const result = await api.upsertPersonalSetting({
        sortComments: originSortComments,
      });
      if (result) {
        personalSetting.value = result;
      }
    };

    /** saltセット */
    const setSalt1 = async (salt: Uint8Array): Promise<void> => {
      randomNumber3.value = Array.from<number>(salt);
    };
    const setSalt2 = async (salt: Uint8Array): Promise<void> => {
      randomNumber1.value = Array.from<number>(salt);
    };
    const setSalt3 = async (salt: Uint32Array): Promise<void> => {
      randomNumber2.value = Array.from<number>(salt);
    };

    const setLoading = async (l: boolean): Promise<void> => {
      loading.value = l;
    };

    /** directトーク情報取得タスク */
    const fetchDirectTalks = async (dId: string): Promise<void> => {
      await GetTalksTask.run(dId);
    };

    return {
      // state
      session,
      domainId,
      topicId,
      messageId,
      navigationGuard,
      lastAccessPath,
      searchParam,
      fromQuery,
      lang,
      device,
      osversion,
      invokerapp,
      invokerversion,
      user_id,
      allowFeature,
      aclMap,
      randomNumber1,
      randomNumber2,
      randomNumber3,
      loading,
      fetching,
      personalSetting,
      // getters
      hasSession,
      salt1,
      salt2,
      salt3,
      userId,
      isMobile: isMobileGetter,
      isAndroid: isAndroidGetter,
      isIos: isIosGetter,
      os,
      isPro,
      me,
      getUser,
      getTopics,
      getTopic,
      getMessages,
      getMessage,
      getComments,
      getComment,
      getAclData,
      isLoading,
      getSortCommentSetting,
      // actions
      setSession,
      setReaction,
      fetchTopics,
      recreateData,
      fetchData: _fetchData,
      fetchDirectDomain,
      fetchDomainInfo,
      syncLoad,
      setDomainId,
      setTopicId,
      setMessageId,
      removeDemoData,
      registerDemoData: registerDemoDataAction,
      setSearchParam,
      getSearchParam,
      setNavigationGuard,
      setDevice,
      setUserId,
      setInvokerApp,
      setFromQuery,
      setLastAccessPath,
      loadAllowFeature,
      setAclData,
      fetchPersonalSetting,
      setSortCommentSetting,
      setSalt1,
      setSalt2,
      setSalt3,
      setLoading,
      fetchDirectTalks,
    };
  },
  {
    persist: {
      // 保存キーはストアID 'app'（旧 vuex-persistedstate の 'vuex' とは別キー）。
      // 旧 'vuex' ブロブからの引き継ぎは store/migrate-legacy.ts が一度だけ実施する（要注意4・案A）。
      key: "app",
      storage: {
        getItem: (key: string): string | null => ls.get(key),
        setItem: (key: string, value: string) => ls.set(key, value),
        removeItem: (key: string) => ls.remove(key),
      } as Storage,
      paths: [
        "lastAccessPath",
        "searchParam",
        "fromQuery",
        "device",
        "invokerapp",
        "user_id",
        "randomNumber1",
        "randomNumber2",
        "randomNumber3",
      ],
    },
  },
);

export default useAppStore;
